<template>
	<div class="cassie-vertical-md mt-1">
		<FilterBar
			:search-query.sync="cookieScriptSearchQuery"
			search-query-label="Service Name"
			:selected-brand-id.sync="selectedAdminPortalBrandId"
			@persistSearchQuery="changeCookieScriptSearchQuery"
			@persistSelectedBrandId="changeAdminPortalBrandFilter"
			@clearFilters="clearFilters"
		>
			<template #after-filters>
				<Dropdown
					v-model="selectedCookieScriptType"
					label="Service Type"
					:items="scriptTypeFilterItems"
					custom-sort
					@change="changeCookieScriptType"
				/>
				<Dropdown
					v-model="selectedCookieCategory"
					label="Service Category"
					:items="categoryFilterItems"
					custom-sort
					@change="changeCookieCategory"
				/>
			</template>
			<template #action>
				<PrimaryActionButton
					v-if="userCanCreateUpdate"
					@click="navigateToCreateScript"
				>
					<v-icon left>
						mdi-plus
					</v-icon>
					Create
				</PrimaryActionButton>
			</template>
		</FilterBar>
		<SectionCard>
			<template #title>
				Services
			</template>
			<template #body>
				<DataTable
					:items="filteredScripts"
					:headers="tableHeaders"
					sort-by="id"
					:sort-desc="true"
					:loading="isLoading"
					@click:row="navigateToCookieScript($event.cookieScriptChannelId)"
				>
					<template #item.cookieScriptName="{ item }">
						{{ item.cookieScriptName }}
						<Chip
							v-if="item.channelGuid"
							class="ml-2 prussian-blue white--text rounded-pill"
						>
							Cassie Default
						</Chip>
					</template>
					<template #item.type="{ item }">
						{{ item.isStrictlyCategory ? 'Strictly Necessary' : 'User Consent' }}
					</template>
					<template #item.usage="{ item }">
						{{ returnUsageCount(item) }}
					</template>
					<template #item.action="{ item }">
						<v-menu
							offset-y
							left
						>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									small
									v-bind="attrs"
									v-on="on"
									@click.stop
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list dense>
								<v-list-item
									v-if="userCanCreateUpdate && userHasBrand(item.brandId) && !item.channelGuid"
									@click="navigateToCookieScript(item.cookieScriptChannelId)"
								>
									<v-list-item-content>
										<v-list-item-title>
											Edit
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-else
									@click="navigateToCookieScript(item.cookieScriptChannelId)"
								>
									<v-list-item-content>
										<v-list-item-title>
											View
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="userCanDelete && !item.countOfAssociatedCookieBanners && !item.countOfAssociatedCookieScripts && userHasBrand(item.brandId) && !item.channelGuid"
									@click.stop.prevent="scriptToRemove = item"
								>
									<v-list-item-content>
										<v-list-item-title>
											Delete
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</v-menu>
					</template>
				</DataTable>
			</template>
		</SectionCard>
		<ConfirmDeleteModal
			v-if="scriptToRemove"
			:entity-name="scriptToRemove.cookieScriptName"
			entity-type="Service"
			@close="scriptToRemove = null"
			@delete="deleteScript"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import FilterBar from '../../../../../../../shared/components/filter-bar.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import ConfirmDeleteModal from '../../../../../../../shared/components/confirm-delete-modal.vue'
import Chip from '../../../../../../../shared/components/chip.vue'
import { useLabels } from '../../../../../../../shared/state/admin-portal-navigation.js'
import { CAN_CREATE_UPDATE_COOKIE_SCRIPTS, CAN_DELETE_COOKIE_SCRIPTS } from '../../../../../../../shared/permissions/admin-portal-permissions.js'
import { deleteCookieScript } from '../../../../../../../shared/utils/api/cookies/cookie-scripts.js'
import { cookieScriptSearchQuery, changeCookieScriptSearchQuery, changeCookieScriptType, selectedCookieScriptType, changeCookieCategory, selectedCookieCategory } from '../../../../../../../shared/state/cookie-scripts.js'
import { selectedAdminPortalBrandId, changeAdminPortalBrandFilter, userHasBrand } from '../../../../../../../shared/state/brands.js'
import { MANAGE_SERVICE } from '../../../../../router/route-names.js'
export default {
	components: {
		DataTable,
		FilterBar,
		PrimaryActionButton,
		SectionCard,
		Dropdown,
		ConfirmDeleteModal,
		Chip
	},
	props: {
		cookieScripts: Array,
		cookieCategories: Array,
		transactionTypes: Array,
		securityLevels: Array,
		channelDeviceCopyActions: Array
	},
	setup () {
		return {
			userHasBrand,
			useLabels,
			cookieScriptSearchQuery,
			changeCookieScriptSearchQuery,
			changeCookieScriptType,
			selectedCookieScriptType,
			changeCookieCategory,
			selectedCookieCategory,
			selectedAdminPortalBrandId,
			changeAdminPortalBrandFilter
		}
	},
	data () {
		return {
			showManageModal: false,
			cookieScriptToEdit: null,
			scriptToRemove: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		isLoading () {
			return !this.cookieScripts || this.cookieScripts.length === 0
		},
		scriptTypeFilterItems () {
			return [
				{
					text: 'View All',
					value: null
				},
				{
					text: 'Essential',
					value: 'Essential'
				},
				{
					text: 'Legitimate Interest',
					value: 'Legitimate Interest'
				},
				{
					text: 'User Consent',
					value: 'User Consent'
				}
			]
		},
		categoryFilterItems () {
			return [
				{
					text: 'View All',
					value: null
				},
				...this.cookieCategories.map(({ id, categoryName, brandName }) => ({
					text: `${categoryName} - ${brandName}`,
					value: id
				})).sort((a, b) => a.text.localeCompare(b.text))
			]
		},
		tableHeaders () {
			return [
				{ value: 'id', text: 'ID', width: '5%' },
				{ value: 'brandName', text: useLabels('Brand'), width: '14%' },
				{ value: 'cookieScriptName', text: 'Service Name', width: '20%' },
				{ value: 'cookieScriptType', text: 'Service Type', width: '12%' },
				{ value: 'cookieCategoryName', text: 'Category Type', width: '16%' },
				{ value: 'usage', text: 'Usage', width: '15%' },
				{ value: 'createdDate', text: 'Created Date', width: '18%' },
				{ value: 'action', text: 'Action', width: '80px', align: 'center', sortable: false }
			]
		},
		filteredScripts () {
			return this.cookieScripts.filter(({ cookieScriptName, brandId, cookieScriptType, cookieCategoryId }) => {
				let check = true
				if (cookieScriptSearchQuery.value) check = cookieScriptName.toLowerCase().includes(cookieScriptSearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && brandId === selectedAdminPortalBrandId.value
				if (selectedCookieScriptType.value !== null) check = check && cookieScriptType === selectedCookieScriptType.value
				if (selectedCookieCategory.value !== null) check = check && cookieCategoryId === selectedCookieCategory.value
				return check
			})
		},
		userCanCreateUpdate () {
			return this.productAreaPermission(CAN_CREATE_UPDATE_COOKIE_SCRIPTS)
		},
		userCanDelete () {
			return this.productAreaPermission(CAN_DELETE_COOKIE_SCRIPTS)
		},
		gtmCookieScripts () {
			return this.cookieScripts.filter(({ gtmEnabled }) => gtmEnabled).map(cookieScript => {
				return {
					cookieCategoryId: cookieScript.cookieCategoryId,
					cookieScriptId: cookieScript.cookieScriptId,
					gtmConsentTypeId: cookieScript.gtmConsentTypeId,
					cookieCategoryName: cookieScript.cookieCategoryName
				}
			})
		}
	},
	methods: {
		returnUsageCount (script) {
			return `${script.countOfAssociatedCookieBanners} Consent Banner${script.countOfAssociatedCookieBanners === 1 ? '' : 's'}`
		},
		clearFilters () {
			selectedCookieScriptType.value = null
			selectedCookieCategory.value = null
		},
		navigateToCreateScript () {
			this.$router.push({
				name: MANAGE_SERVICE
			})
		},
		navigateToCookieScript (cookieScriptChannelId) {
			this.$router.push({
				name: MANAGE_SERVICE,
				query: { cookieScriptChannelId }
			})
		},
		async deleteScript () {
			await deleteCookieScript(this.scriptToRemove.cookieScriptChannelId)
			this.scriptToRemove = null
			this.$emit('load')
		}
	}
}
</script>
